import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Container } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const processCallback = async () => {
      console.log('AuthCallback processCallback running');
      // Get token or error from URL query parameters
      const token = searchParams.get('token');
      const error = searchParams.get('error');

      if (error) {
        // Handle error from backend
        setStatus('error');
        setErrorMessage(decodeURIComponent(error));
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/?error=' + encodeURIComponent(error));
        }, 3000);
        return;
      }

      if (!token) {
        // No token and no error - invalid callback
        setStatus('error');
        setErrorMessage('Invalid authentication callback. No token received.');
        
        setTimeout(() => {
          navigate('/?error=invalid_callback');
        }, 3000);
        return;
      }

      try {
        // Store token and authenticate user (set freshLogin flag)
        await loginWithToken(token, true);
        
        setStatus('success');
        
        // Redirect to dashboard after brief success message
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } catch (err: any) {
        console.error('Error processing authentication:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Failed to complete authentication');
        
        setTimeout(() => {
          navigate('/?error=auth_failed');
        }, 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate, loginWithToken]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            p: 6,
            textAlign: 'center',
          }}
        >
          {status === 'processing' && (
            <>
              <CircularProgress size={60} sx={{ color: '#8b5cf6', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Authenticating...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we complete your sign-in
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle sx={{ fontSize: 60, color: '#10b981', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#10b981">
                Authentication Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Redirecting to dashboard...
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: '#ef4444', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#ef4444">
                Authentication Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                {errorMessage}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Redirecting to login page...
              </Typography>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AuthCallback;

